import type {
  Equipment,
  EquipmentWithHistory,
  Inspection,
  InspectionFollowUpAction,
  InspectionResultChoice,
} from '@geartracker/types';
import {
  ExpirationStatus,
  GeneralStatus,
  HardwareStatus,
  InspectionResult,
  InspectionStatus,
} from '@geartracker/types';

import { addMonths, subDays } from 'date-fns';

/** In days */
const INSPECTION_WARNING_DAYS = 45;
/** In days */
const EXPIRATION_WARNING_DAYS = 90;

type EquipmentExpirationInputs = Pick<
  Equipment,
  | 'manufactureDate'
  | 'manufactureLifeSpan'
  | 'commissioningDate'
  | 'commissioningLifeSpan'
>;

type EquipmentDeadlineInputs = EquipmentExpirationInputs &
  Pick<Equipment, 'createdAt' | 'lastInspectionDate' | 'inspectionPeriod'>;

type EquipmentStatusInputs = Pick<
  Equipment,
  'nextInspectionDate' | 'expirationDate' | 'hardwareStatus' | 'userId'
>;

type EquipmentDeadlines = {
  expirationDate: Date | null;
  nextInspectionDate: Date | null;
};

export type ComputedEquipmentState = EquipmentDeadlines & {
  hasExpiration: boolean;
  lastInspectionDate: Date | null;
  hardwareStatus: HardwareStatus;
  expirationStatus: ExpirationStatus;
  inspectionStatus: InspectionStatus;
  generalStatus: GeneralStatus;
};

const HARDWARE_STATUS_VALUES = new Set(Object.values(HardwareStatus));

function toNullableDate(value: Date | string | null) {
  return value ? new Date(value) : null;
}

function isHardwareStatus(
  value: GeneralStatus | HardwareStatus | null,
): value is HardwareStatus {
  return value !== null && HARDWARE_STATUS_VALUES.has(value as HardwareStatus);
}

function computeInspectionStatus(
  nextInspectionDate: Date | null,
  now: Date,
): InspectionStatus {
  if (!nextInspectionDate) {
    return InspectionStatus.UP_TO_DATE;
  }

  if (now >= nextInspectionDate) {
    return InspectionStatus.LATE;
  }

  const inspectionWarningThreshold = subDays(
    nextInspectionDate,
    INSPECTION_WARNING_DAYS,
  );

  if (now >= inspectionWarningThreshold) {
    return InspectionStatus.NOTIFY;
  }

  return InspectionStatus.UP_TO_DATE;
}

function computeExpirationStatus(
  expirationDate: Date | null,
  now: Date,
): ExpirationStatus {
  if (!expirationDate) {
    return ExpirationStatus.VALID;
  }

  if (now >= expirationDate) {
    return ExpirationStatus.EXPIRED;
  }

  const expirationWarningThreshold = subDays(
    expirationDate,
    EXPIRATION_WARNING_DAYS,
  );

  if (now >= expirationWarningThreshold) {
    return ExpirationStatus.NOTIFY;
  }

  return ExpirationStatus.VALID;
}

/**
 * A terminal hardware status short-circuits the general status only when the
 * equipment is truly frozen in place. `DAMAGED` keeps participating in the
 * deadline-based evaluation because repair tracking is now orthogonal to
 * inspection / expiration compliance. The exhaustive switch forces a compile-time
 * decision whenever a new `HardwareStatus` is added.
 */
function isTerminalHardwareStatus(hardwareStatus: HardwareStatus): boolean {
  switch (hardwareStatus) {
    case HardwareStatus.IN_STOCK:
    case HardwareStatus.DAMAGED:
      return false;
    case HardwareStatus.QUARANTINE:
    case HardwareStatus.LOST:
    case HardwareStatus.TRASH:
      return true;
    default: {
      const _exhaustive: never = hardwareStatus;
      return _exhaustive;
    }
  }
}

function computeGeneralStatus(
  hardwareStatus: HardwareStatus,
  inspectionStatus: InspectionStatus,
  expirationStatus: ExpirationStatus,
  isAssigned: boolean,
): GeneralStatus {
  if (isTerminalHardwareStatus(hardwareStatus)) {
    return hardwareStatus;
  }

  if (
    inspectionStatus === InspectionStatus.LATE ||
    expirationStatus === ExpirationStatus.EXPIRED
  ) {
    return GeneralStatus.NOT_CONFORM;
  }

  if (
    inspectionStatus === InspectionStatus.NOTIFY ||
    expirationStatus === ExpirationStatus.NOTIFY
  ) {
    return GeneralStatus.NOTIFY;
  }

  if (hardwareStatus === HardwareStatus.DAMAGED) {
    return HardwareStatus.DAMAGED;
  }

  if (!isAssigned) {
    return GeneralStatus.IN_STOCK;
  }

  return GeneralStatus.CONFORM;
}

export function deriveHasExpiration(
  equipment: Pick<Equipment, 'manufactureLifeSpan' | 'commissioningLifeSpan'>,
) {
  return Boolean(
    equipment.manufactureLifeSpan || equipment.commissioningLifeSpan,
  );
}

/**
 * Resolves the commissioning date for an equipment.
 *
 * An equipment is commissioned as soon as it is assigned to a user. Once
 * defined, the commissioning date is immutable, except when a planned future
 * date exists and assignment happens before it: the real assignment date wins.
 * If there is no current date, an explicitly requested date wins unless it is
 * still in the future while assigned, otherwise we commission it on `now` when
 * it is being assigned to a user (and leave it null when it stays unassigned).
 */
export function resolveCommissioningDate({
  requested,
  current = null,
  isAssigned,
  now = new Date(),
}: {
  requested?: Date | string | null;
  current?: Date | string | null;
  isAssigned: boolean;
  now?: Date;
}): Date | null {
  if (current != null) {
    const currentDate = new Date(current);
    return isAssigned && currentDate > now ? now : currentDate;
  }

  if (requested != null) {
    const requestedDate = new Date(requested);
    return isAssigned && requestedDate > now ? now : requestedDate;
  }

  return isAssigned ? now : null;
}

/**
 * An equipment expires at the earliest of its manufacture-anchored and
 * commissioning-anchored limits. This returns the cycle that actually binds
 * (the earliest expiration; manufacture wins ties) so deadline computation and
 * UI share one source of truth — the UI can show a consistent
 * `anchor → lifespan → expiration` line instead of mixing one cycle's anchor
 * with the other cycle's expiration. Returns `null` when neither cycle can
 * expire (no date+lifespan pair).
 */
export function getBindingLifespanCycle(equipment: EquipmentExpirationInputs) {
  const manufacture =
    equipment.manufactureDate && equipment.manufactureLifeSpan
      ? {
          anchor: new Date(equipment.manufactureDate),
          isCommissioning: false as const,
          months: equipment.manufactureLifeSpan,
          expiration: addMonths(
            new Date(equipment.manufactureDate),
            equipment.manufactureLifeSpan,
          ),
        }
      : null;
  const commissioning =
    equipment.commissioningDate && equipment.commissioningLifeSpan
      ? {
          anchor: new Date(equipment.commissioningDate),
          isCommissioning: true as const,
          months: equipment.commissioningLifeSpan,
          expiration: addMonths(
            new Date(equipment.commissioningDate),
            equipment.commissioningLifeSpan,
          ),
        }
      : null;

  if (!manufacture) {
    return commissioning;
  }
  if (!commissioning) {
    return manufacture;
  }
  return manufacture.expiration <= commissioning.expiration
    ? manufacture
    : commissioning;
}

/**
 * The date the next-inspection countdown starts from: the last inspection if
 * any, otherwise the commissioning date, otherwise the creation date. Shared by
 * the deadline computation and the progress UI so both anchor on the same date.
 */
export function getInspectionReferenceDate(
  equipment: Pick<
    Equipment,
    'lastInspectionDate' | 'commissioningDate' | 'createdAt'
  >,
): Date {
  return (
    toNullableDate(equipment.lastInspectionDate) ??
    toNullableDate(equipment.commissioningDate) ??
    new Date(equipment.createdAt)
  );
}

export function computeDeadlines(
  equipment: EquipmentDeadlineInputs,
): EquipmentDeadlines {
  const inspectionReferenceDate = getInspectionReferenceDate(equipment);

  const expirationDate = getBindingLifespanCycle(equipment)?.expiration ?? null;

  const nextInspectionDate = equipment.inspectionPeriod
    ? addMonths(inspectionReferenceDate, equipment.inspectionPeriod)
    : null;

  return {
    expirationDate,
    nextInspectionDate,
  };
}

export function hardwareStatusToInspectionEventGeneralStatus(
  hardwareStatus: HardwareStatus,
): GeneralStatus {
  return hardwareStatus === HardwareStatus.IN_STOCK
    ? GeneralStatus.CONFORM
    : hardwareStatus;
}

export function inspectionEventGeneralStatusToHardwareStatus(
  generalStatus: GeneralStatus | null,
): HardwareStatus {
  if (generalStatus === null || generalStatus === GeneralStatus.CONFORM) {
    return HardwareStatus.IN_STOCK;
  }

  if (generalStatus === GeneralStatus.IN_STOCK) {
    return HardwareStatus.IN_STOCK;
  }

  if (isHardwareStatus(generalStatus)) {
    return generalStatus;
  }

  throw new Error(
    `Unexpected inspection event general status: ${generalStatus}`,
  );
}

/**
 * Repair v3 — derive the equipment HardwareStatus from an inspection verdict and
 * its follow-up action. This is the single source of truth shared by the inspect
 * endpoint. DAMAGED/QUARANTINE are only reached through OPEN_REPAIR (the repair is
 * what actually withdraws or flags the item); TRASH/LOST are set directly.
 *
 * choice === 'LOST' means the equipment could not be inspected: its follow-up
 * action picks the actual outcome (TRASH, a non-blocking OPEN_REPAIR, or a
 * genuine LOST) instead of always being LOST.
 */
export function inspectionResultToHardwareStatus(
  choice: InspectionResultChoice,
  followUpAction: InspectionFollowUpAction,
): HardwareStatus {
  if (choice === 'LOST') {
    if (followUpAction === 'TRASH') {
      return HardwareStatus.TRASH;
    }
    if (followUpAction === 'OPEN_REPAIR') {
      return HardwareStatus.DAMAGED;
    }
    return HardwareStatus.LOST;
  }
  if (followUpAction === 'TRASH') {
    return HardwareStatus.TRASH;
  }
  if (followUpAction === 'OPEN_REPAIR') {
    return choice === InspectionResult.FAIL
      ? HardwareStatus.QUARANTINE
      : HardwareStatus.DAMAGED;
  }
  return HardwareStatus.IN_STOCK;
}

/**
 * Maps a legacy per-inspection HardwareStatus (as still entered in the equipment
 * create/edit form's `inspections` array, and by the CSV import) to a
 * standardised InspectionResult. LOST is deliberately not an inspection result
 * (see InspectionResult's doc comment): callers must filter LOST entries out
 * before creating EquipmentInspection rows, this throws instead of silently
 * collapsing LOST into FAIL.
 */
export function hardwareStatusToInspectionResult(
  status: HardwareStatus,
): InspectionResult {
  switch (status) {
    case HardwareStatus.DAMAGED:
      return InspectionResult.CONFORM_WITH_RESERVE;
    case HardwareStatus.QUARANTINE:
    case HardwareStatus.TRASH:
      return InspectionResult.FAIL;
    case HardwareStatus.LOST:
      throw new Error(
        'LOST is not an inspection result: filter it out before mapping',
      );
    default:
      return InspectionResult.CONFORM;
  }
}

export function getLastInspection(
  inspections: Inspection[],
): Inspection | null {
  if (inspections.length === 0) {
    return null;
  }

  // O(N) sans mutation
  return inspections.reduce((latest, current) =>
    current.date.getTime() > latest.date.getTime() ? current : latest,
  );
}

export function computeEquipmentStatuses(
  equipment: EquipmentStatusInputs,
  now = new Date(),
) {
  const nextInspectionDate = toNullableDate(equipment.nextInspectionDate);
  const expirationDate = toNullableDate(equipment.expirationDate);
  const inspectionStatus = computeInspectionStatus(nextInspectionDate, now);
  const expirationStatus = computeExpirationStatus(expirationDate, now);
  const generalStatus = computeGeneralStatus(
    equipment.hardwareStatus,
    inspectionStatus,
    expirationStatus,
    equipment.userId != null,
  );

  return {
    expirationStatus,
    inspectionStatus,
    generalStatus,
  };
}

export function computeEquipmentState(
  equipment: EquipmentDeadlineInputs &
    Pick<Equipment, 'hardwareStatus' | 'userId'>,
  now = new Date(),
): ComputedEquipmentState {
  const lastInspectionDate = toNullableDate(equipment.lastInspectionDate);
  const hasExpiration = deriveHasExpiration(equipment);
  const deadlines = computeDeadlines({
    ...equipment,
    lastInspectionDate,
  });
  const statuses = computeEquipmentStatuses(
    {
      ...deadlines,
      hardwareStatus: equipment.hardwareStatus,
      userId: equipment.userId,
    },
    now,
  );

  return {
    hasExpiration,
    ...deadlines,
    ...statuses,
    lastInspectionDate,
    hardwareStatus: equipment.hardwareStatus,
  };
}

function hasDateChanged(
  current: Date | string | null,
  next: Date | string | null,
) {
  return toNullableDate(current)?.getTime() !== toNullableDate(next)?.getTime();
}

export function hasEquipmentStateChanged(
  current: Pick<
    Equipment,
    | 'hasExpiration'
    | 'expirationDate'
    | 'nextInspectionDate'
    | 'lastInspectionDate'
    | 'hardwareStatus'
    | 'expirationStatus'
    | 'inspectionStatus'
    | 'generalStatus'
  >,
  nextState: ComputedEquipmentState,
) {
  return (
    current.hasExpiration !== nextState.hasExpiration ||
    hasDateChanged(current.expirationDate, nextState.expirationDate) ||
    hasDateChanged(current.nextInspectionDate, nextState.nextInspectionDate) ||
    hasDateChanged(current.lastInspectionDate, nextState.lastInspectionDate) ||
    current.hardwareStatus !== nextState.hardwareStatus ||
    current.expirationStatus !== nextState.expirationStatus ||
    current.inspectionStatus !== nextState.inspectionStatus ||
    current.generalStatus !== nextState.generalStatus
  );
}

/** Raw EventLog rows as included on the equipment payload (Prisma relation `EventLogs`). */
type RawTimelineEventLog = {
  id: number;
  type: string;
  createdAt: Date | string;
  ownerUser?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    externalUserId: string | null;
  } | null;
  user?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    externalUserId: string | null;
  } | null;
  repair?: { id: number } | null;
  equipmentInspection?: {
    id: number;
    result: InspectionResult;
    comment: string | null;
  } | null;
  deletedItemName?: string | null;
};

/**
 * Repair v3 — finalize an equipment for the API response: `eventLogs` is the
 * EventLog-based timeline consumed by the client, built from the included
 * `EventLogs` relation plus synthetic MANUFACTURING/COMMISSIONING markers.
 * EquipmentEvent is a frozen backup table and is no longer read here.
 */
export function enrichEquipmentHistory(
  equipment: Omit<EquipmentWithHistory, 'eventLogs' | 'lastLocation'> & {
    EventLogs?: RawTimelineEventLog[];
    lastLocationLat: number | null;
    lastLocationLng: number | null;
    lastLocationAccuracy: number | null;
    lastLocationAt: Date | string | null;
  },
) {
  const lastLocation: EquipmentWithHistory['lastLocation'] =
    equipment.lastLocationLat != null &&
    equipment.lastLocationLng != null &&
    equipment.lastLocationAt != null
      ? {
          latitude: equipment.lastLocationLat,
          longitude: equipment.lastLocationLng,
          accuracy: equipment.lastLocationAccuracy,
          capturedAt: new Date(equipment.lastLocationAt),
        }
      : null;

  const eventLogs: EquipmentWithHistory['eventLogs'] = (
    equipment.EventLogs ?? []
  ).map((log) => ({
    id: log.id,
    type: log.type as EquipmentWithHistory['eventLogs'][number]['type'],
    createdAt: new Date(log.createdAt),
    ownerUser: log.ownerUser ?? null,
    user: log.user ?? null,
    repair: log.repair ?? null,
    equipmentInspection: log.equipmentInspection ?? null,
    deletedItemName: log.deletedItemName ?? null,
  }));

  if (equipment.commissioningDate) {
    eventLogs.push({
      id: null,
      type: 'COMMISSIONING',
      createdAt: new Date(equipment.commissioningDate),
    });
  }

  if (equipment.manufactureDate) {
    eventLogs.push({
      id: null,
      type: 'MANUFACTURING',
      createdAt: new Date(equipment.manufactureDate),
    });
  }

  eventLogs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  // The `EventLogs` relation and flat lastLocation* columns are replaced by
  // the normalised `eventLogs` / `lastLocation` fields.
  const {
    EventLogs: _raw,
    lastLocationLat: _lat,
    lastLocationLng: _lng,
    lastLocationAccuracy: _accuracy,
    lastLocationAt: _at,
    ...rest
  } = equipment;
  return {
    ...rest,
    eventLogs,
    lastLocation,
  };
}
